/**
 * Persistence for the runtime brand config.
 *
 * The cached document is what makes a cold start with a dead/slow admin host a
 * NO-OP instead of a visible regression: the operator's last known-good theme
 * and feeds are applied synchronously at module-init, long before the network
 * has been consulted.
 *
 * MMKV is synchronous on every platform (JSI on native, `localStorage` on
 * web), which is the whole reason this can run at import time — see
 * `#/lib/brand/index`. Every access is wrapped: storage that is unavailable
 * (SSR, a locked-down browser, a quota error) must degrade to "no cache", never
 * throw into the module graph.
 */

import {type BrandRuntimeConfig, validateBrandConfig} from '#/lib/brand/runtime'
import {device} from '#/storage'

/**
 * Read the last known-good config.
 *
 * The cache is RE-VALIDATED on every read, not trusted: it may have been
 * written by an older build with different rules, hand-edited in devtools, or
 * corrupted. A stale/malformed record is discarded exactly like a malformed
 * network response.
 */
export function readCachedBrandConfig(
  brandId: string,
): BrandRuntimeConfig | null {
  try {
    const raw = device.get(['brandConfig'])
    if (raw === undefined) return null
    return validateBrandConfig(raw, brandId)
  } catch {
    return null
  }
}

/** Persist a validated config for the next cold start. Never throws. */
export function writeCachedBrandConfig(config: BrandRuntimeConfig): void {
  try {
    device.set(['brandConfig'], config)
  } catch {}
}

/** Drop the cache (used when the document is no longer servable/valid). */
export function clearCachedBrandConfig(): void {
  try {
    device.remove(['brandConfig'])
  } catch {}
}
